/**
 * What Now derives from the cloud's answers, pure: the integrations that keep failing (the cloud's streaks,
 * each with why in a few words from its newest failed document), the 24 h bars of the sparkline, and the
 * rhythm list below the fold. One incident has one truth: the map's labels, the feed's pinned line and the
 * rhythm list all read `activity.failing`; only a cloud without it gets the streaks derived here, from the
 * newest failed documents against the runs' starts. No React, no DOM: tested with node --test.
 */
import dayjs from 'dayjs';
import type { Activity, Failing, Flow, FlowRhythm, Hit, HistogramBucket } from '../lib/api.ts';
import { count, duration } from '../lib/format.ts';
import { age } from '../lib/freshness.ts';
import { late, rhythmOf, type Rhythm } from '../lib/rhythm.ts';
import { stripSystem, systemName, systemOf } from '../lib/systems.ts';
import { noun } from '../lib/nouns.ts';

export const HOUR = 3_600_000;

/** A failing integration as the cloud reports it (since when, how many wait), with why from its newest failed document. */
export interface Incident extends Failing { reason: string | null }

const STATUS_TEXT = /\b(\d{3})\s+(?:Unauthorized|Forbidden|Not Found|Conflict|Bad Request|Internal Server Error|Bad Gateway|Service Unavailable|Gateway Timeout|Too Many Requests|Unprocessable Entity|Method Not Allowed)\b/gi;

/**
 * Why, in a few words, from a document's info: what follows "failed" with the HTTP status text dropped
 * and parentheses turned into a clause — "Invoice 1 for SO-1 failed: Maventa 401 Unauthorized (token
 * expired)" → "Maventa 401, token expired".
 */
export function reasonOf(info: string | null | undefined): string | null {
  if (!info) return null;
  const m = /\bfailed\b[:\s—–-]*(.+)$/i.exec(info);
  let r = m?.[1] ?? (info.includes(': ') ? info.slice(info.lastIndexOf(': ') + 2) : '');
  r = r.replace(STATUS_TEXT, '$1').replace(/\s*\(([^()]+)\)/g, ', $1').replace(/\s*:\s+/g, ', ').replace(/\s+/g, ' ').trim();
  return r.replace(/[.,;]+$/, '') || null;
}

/**
 * The integrations failing now, with why: the cloud's streaks, each with the reason read off its newest
 * failed document among the ones searched for (the streak itself names the document, the search carries
 * its words). The longest first.
 */
export function incidentsOf(a: Activity, failed: Hit[]): Incident[] {
  return (a.failing ?? [])
    .map((f) => {
      const mine = failed.filter((h) => h.meta.process === f.process).sort((x, y) => y.timestamp - x.timestamp);
      const newest = mine.find((h) => h.uuid === f.newestRef.uuid) ?? mine[0];
      return { ...f, reason: reasonOf(newest?.meta.info) };
    })
    .sort((x, y) => x.since - y.since);
}

/**
 * What an incident is about, in the words of its flow: "invoices to the e-invoice operator" (the documents
 * that go in, where they were going), or the integration's own name for a process the activity has no flow of.
 */
export function incidentWhat(f: Failing, flow: Flow | undefined, systemOfCls: Map<string, string>): string {
  if (!flow) return stripSystem(f.process).toLowerCase();
  const to = flow.to.find((t) => t !== flow.from);
  const target = to ? systemName(systemOfCls.get(to) ?? to) : targetOf(f.process);
  return `${noun(flow.from, 2)}${target ? ` to ${target}` : ''}`;
}

/** The second line of the hero: one incident as a phrase, linking to the stage of its process. */
export interface Headline { process: string; text: string }

/**
 * Under the hour's sentence, the streaks that are failing right now: "invoices to the e-invoice operator
 * failing since 17:37 (7 invoices waiting)" — with the weekday when the streak began on another day.
 * Empty when nothing is failing anywhere.
 */
export function headlines(a: Activity, now: number): Headline[] {
  const flows = new Map(a.flows.map((f) => [f.process, f]));
  const systemOfCls = new Map(a.byClass.map((c) => [c.cls, c.system]));
  return [...(a.failing ?? [])]
    .sort((x, y) => x.since - y.since)
    .map((f) => {
      const flow = flows.get(f.process);
      const d = dayjs(f.since);
      const since = d.isSame(dayjs(now), 'day') ? d.format('HH:mm') : d.format('ddd HH:mm');
      const waiting = `${count(f.count)} ${flow ? noun(flow.from, f.count) : f.count === 1 ? 'document' : 'documents'} waiting`;
      return { process: f.process, text: `${incidentWhat(f, flow, systemOfCls)} failing since ${since} (${waiting})` };
    });
}

/**
 * The fallback for a cloud without `activity.failing`: the integrations whose newest run has a failed
 * document, and so do the runs before it back to `since` (a run is the window between two starts), as
 * far as the failed documents asked for reach. The same shape as the cloud's, so nothing downstream knows.
 */
export function failingOf(flows: Flow[], failed: Hit[], now: number): Failing[] {
  const out: Failing[] = [];
  for (const f of flows) {
    if (f.failed === 0) continue;
    const mine = failed.filter((h) => h.meta.process === f.process).sort((a, b) => b.timestamp - a.timestamp);
    if (!mine.length) continue;
    const starts = [...new Set(f.starts)].sort((a, b) => b - a);
    let since: number | null = null;
    for (let i = 0; i < starts.length; i++) {
      const from = starts[i] - 1000;
      const to = i === 0 ? now + HOUR : starts[i - 1] - 1000;
      if (!mine.some((h) => h.timestamp >= from && h.timestamp < to)) break;
      since = starts[i];
    }
    if (since === null) continue;
    const docs = mine.filter((h) => h.timestamp >= since - 1000);
    const h = mine[0];
    out.push({ process: f.process, system: f.system ?? systemOf(f.process), since, count: docs.length, newestRef: { vaultId: h.vaultId, uuid: h.uuid, day: h.day } });
  }
  return out.sort((a, b) => a.since - b.since);
}

export interface Bar { time: number; count: number; failed: number }

/** The last 24 hours as hourly bars (the current hour last, still filling), from the activity's finer buckets. */
export function hourlyBars(all: HistogramBucket[], failed: HistogramBucket[], now: number): Bar[] {
  const end = Math.floor(now / HOUR) * HOUR;
  const bars: Bar[] = Array.from({ length: 24 }, (_, i) => ({ time: end - (23 - i) * HOUR, count: 0, failed: 0 }));
  const add = (buckets: HistogramBucket[], key: 'count' | 'failed') => {
    for (const b of buckets) {
      const i = 23 + Math.floor((b.time - end) / HOUR);
      if (i >= 0 && i < 24) bars[i][key] += b.count;
    }
  };
  add(all, 'count');
  add(failed, 'failed');
  return bars;
}

/** "the data warehouse", "ERP", "customers": where a process says it sends to, as a phrase. */
export function targetOf(process: string): string | null {
  const t = /\bto\s+(.+)$/i.exec(stripSystem(process))?.[1]?.trim();
  if (!t) return null;
  if (/^[A-Z0-9 ]+$/.test(t)) return t;
  const low = t.toLowerCase();
  return /^the\b/.test(low) || /s$/.test(low) ? low : `the ${low}`;
}

/** The rhythm of an integration: the cloud's, from a week of starts (a daily job ran once in the window), else the window's. */
export const rhythmOfFlow = (f: Flow): Rhythm | FlowRhythm => f.rhythm ?? rhythmOf(f.starts);

/** "⏱ daily 18:00" · "⏱ hourly :02" · "⚡ ≈ 42 runs a day" · "⚡ ran once": the rhythm as a chip, counts labelled. */
export function rhythmChip(r: Rhythm): string {
  if (r.scheduled) return `⏱ ${r.short}`;
  if (r.kind === 'once') return `⚡ ${r.label}`;
  const m = /about (\d+) (a day|an hour|a week)/.exec(r.label);
  return m ? `⚡ ≈ ${m[1]} runs ${m[2]}` : `⚡ ${r.label.replace(/^on events,\s*/, '')}`;
}

/** Words with the numbers, times and durations in them as their own parts: those are set in mono, the words in sans. */
export type Words = (string | { mono: string })[];

// a number (count() groups thousands with a no-break space), a time (18:00, :02) or a duration (3.6 h, 25 min, 2 d)
const NUMBERS = /(?:\d(?:[\d.,]|[  ](?=\d))*|:\d{2})(?::\d{2})*(?:\s?(?:ms|s|min|h|d)\b)?/g;

/** "≈ 38 runs a day" → ["≈ ", {mono: "38"}, " runs a day"]; "failing 3.6 h · 6 invoices waiting" keeps "3.6 h" and "6" in mono. */
export function words(text: string): Words {
  const out: Words = [];
  let last = 0;
  for (const m of text.matchAll(NUMBERS)) {
    const i = m.index ?? 0;
    if (i > last) out.push(text.slice(last, i));
    out.push({ mono: m[0] });
    last = i + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const dueWord = (next: number, now: number) => {
  const d = dayjs(next);
  if (d.isSame(dayjs(now), 'day')) return d.format('HH:mm');
  if (d.isSame(dayjs(now).add(1, 'day'), 'day')) return `tomorrow ${d.format('HH:mm')}`;
  return d.format('ddd HH:mm');
};

export interface RhythmRow {
  process: string;
  system: string;
  name: string;
  /** The rhythm as a chip ("⏱ daily 18:00", "⚡ ≈ 38 runs a day"); render it through words() so only the numbers are mono. */
  chip: string;
  runs: number;
  last: number | null;
  /** What to say at the end of the row: failing · late · silent · next due, or nothing for an event-driven flow that is fine. */
  status: { text: string; tone: 'fail' | 'warn' | null } | null;
}

/**
 * One row per integration, the ones that need a look first: failing, late, silent, then schedules, then the
 * busiest. A failing row says what the feed says — how long, and how many wait — from the cloud's streak.
 */
export function rhythmRows(a: Activity, now: number): RhythmRow[] {
  const rank = (s: RhythmRow['status'], r: Rhythm) => (s?.tone === 'fail' ? 0 : s?.tone === 'warn' ? 1 : r.scheduled ? 2 : 3);
  const rows = a.flows.map((f) => {
    const r = rhythmOfFlow(f);
    const fail = a.failing?.find((x) => x.process === f.process);
    const silent = a.silent.find((s) => s.process === f.process);
    const status: RhythmRow['status'] = fail
      ? { text: `failing ${duration(Math.max(60_000, now - fail.since))} · ${count(fail.count)} ${noun(f.from, fail.count)} waiting`, tone: 'fail' }
      : late(r, now) && r.next
        ? { text: `late — was due ${age(r.next, now)} ago`, tone: 'fail' }
        : silent
          ? { text: `silent ${duration(Math.max(60_000, now - silent.last))} · usually every ${duration(silent.usualMs)}`, tone: 'warn' }
          : r.scheduled && r.next
            ? { text: `next due ${dueWord(r.next, now)}`, tone: null }
            : null;
    return { row: { process: f.process, system: f.system ?? systemOf(f.process) ?? '', name: stripSystem(f.process), chip: rhythmChip(r), runs: f.runs, last: f.last || null, status }, r };
  });
  return rows.sort((x, y) => rank(x.row.status, x.r) - rank(y.row.status, y.r) || y.r.perDay - x.r.perDay || y.row.runs - x.row.runs).map((x) => x.row);
}
